import { gql } from '@apollo/client'

// ---- Auth ---------------------------------------------------------

export const LOGIN_MUTATION = gql`
  mutation StudioLogin($email: String!, $password: String!) {
    userLogin(email: $email, password: $password) {
      token
      user {
        id
        email
        fullName
        role
        canSell
        canManageMall
        mall { id name }
        store {
          id
          name
        }
      }
      userErrors { field message }
    }
  }
`

// ---- Livestreams (list + per-store filter) -------------------------

export const GET_LIVESTREAMS = gql`
  query StudioGetLivestreams(
    $first: Int
    $sortBy: String
    $sortOrder: String
  ) {
    livestreams(first: $first, sortBy: $sortBy, sortOrder: $sortOrder) {
      id
      title
      description
      status
      startedAt
      endedAt
      viewerCount
      duration
      isLive
      isUpcoming
      isEnded
      thumbnailUrl
      store {
        id
        name
        user { id fullName }
      }
      livestreamProducts {
        id
        position
        isFeatured
        product { id title }
      }
    }
  }
`

// ---- Livestream lifecycle -----------------------------------------

export const CREATE_LIVESTREAM = gql`
  mutation StudioCreateLivestream(
    $storeId: ID!
    $title: String!
    $description: String
    $startedAt: ISO8601DateTime!
  ) {
    createLivestream(
      storeId: $storeId
      title: $title
      description: $description
      startedAt: $startedAt
    ) {
      livestream {
        id
        title
        status
        startedAt
      }
      userErrors { field message }
    }
  }
`

export const START_LIVESTREAM = gql`
  mutation StudioStartLivestream($id: ID!) {
    startLivestream(id: $id) {
      livestream { id title status }
      publisherToken
      wsUrl
      userErrors { field message }
    }
  }
`

export const END_LIVESTREAM = gql`
  mutation StudioEndLivestream($id: ID!) {
    endLivestream(id: $id) {
      livestream { id status endedAt }
      userErrors { field message }
    }
  }
`

// ---- Product picker ----------------------------------------------

export const GET_STORE_PRODUCTS = gql`
  query StudioGetStoreProducts($storeId: ID!) {
    store(id: $storeId) {
      id
      storeProducts {
        id
        product {
          id
          title
          imageUrls
          status
        }
      }
    }
  }
`

export const ADD_LIVESTREAM_PRODUCT = gql`
  mutation StudioAddLivestreamProduct(
    $livestreamId: ID!
    $productId: ID!
    $position: Int
  ) {
    livestreamProductAdd(
      livestreamId: $livestreamId
      productId: $productId
      position: $position
      featured: false
    ) {
      livestreamProduct { id position }
      userErrors { field message }
    }
  }
`

// ---- Buyer PDP (public) -------------------------------------------
//
// product(id:) is guest-readable: with no mall context the resolver
// returns the active product across all malls, so this loads for an
// anonymous visitor with no Authorization header. `stores { id }`
// yields the store_id the voice-avatar session needs — the same field
// the iOS PDP reads to surface "Talk to assistant".

// ---- Shopper catalog + cart + checkout ----------------------------
//
// All guest-capable: the cart mutations/query accept an optional
// guestToken (the server mints one on first add and echoes it back),
// and createOrder does guest checkout with guestToken + email. None of
// this required a backend change — the API already supported it.

export const GET_SHOP_PRODUCTS = gql`
  query ShopProducts($first: Int) {
    products(first: $first) {
      edges {
        node {
          id
          title
          handle
          currency
          imageUrls
          # NB: deliberately NOT using priceRange — its resolver 500s on
          # the products connection for some products. Compute min/max
          # from the (cheap) variant prices client-side instead.
          productVariants { id price }
        }
      }
    }
  }
`

export const GET_CART = gql`
  query ShopCart($guestToken: String) {
    cart(guestToken: $guestToken) {
      id
      quantity
      totalPrice
      inStock
      availableQuantity
      product { id title imageUrls currency }
      productVariant { id title price option1 }
    }
  }
`

export const ADD_TO_CART = gql`
  mutation ShopAddToCart($productVariantId: ID!, $quantity: Int!, $guestToken: String) {
    addToCart(productVariantId: $productVariantId, quantity: $quantity, guestToken: $guestToken) {
      cartItem { id quantity }
      guestToken
      userErrors { field message }
    }
  }
`

export const UPDATE_CART_ITEM = gql`
  mutation ShopUpdateCartItem($cartItemId: ID!, $quantity: Int!, $guestToken: String) {
    updateCartItem(cartItemId: $cartItemId, quantity: $quantity, guestToken: $guestToken) {
      cartItem { id quantity }
      userErrors { field message }
    }
  }
`

export const REMOVE_FROM_CART = gql`
  mutation ShopRemoveFromCart($cartItemId: ID!, $guestToken: String) {
    removeFromCart(cartItemId: $cartItemId, guestToken: $guestToken) {
      success
      userErrors { field message }
    }
  }
`

export const CREATE_ORDER = gql`
  mutation ShopCreateOrder(
    $guestToken: String
    $email: String
    $shippingAddress: JSON
  ) {
    createOrder(
      guestToken: $guestToken
      email: $email
      shippingAddress: $shippingAddress
    ) {
      order { id orderNumber email status total currency }
      claimToken
      userErrors { field message }
    }
  }
`

// ---- Storefront home (marketplace landing) ------------------------
//
// One round-trip for the whole home: featured products (special offers),
// live / upcoming / past livestreams, and a catalog slice we derive the
// brand + mall rails from client-side (there's no public stores/malls
// list query, but products expose their store + mall).

export const GET_STOREFRONT_HOME = gql`
  query StorefrontHome {
    featured: featuredProducts(limit: 8) {
      id
      title
      currency
      imageUrls
      productVariants { id price }
    }
    live: livestreams(status: ["live"], first: 8) {
      ...StreamCard
    }
    upcoming: livestreams(status: ["scheduled"], first: 8, sortBy: "started_at", sortOrder: "asc") {
      ...StreamCard
    }
    past: livestreams(status: ["ended"], first: 8, sortBy: "started_at", sortOrder: "desc") {
      ...StreamCard
    }
    catalog: products(first: 24) {
      edges {
        node {
          id
          title
          currency
          imageUrls
          productVariants { id price }
          stores { id name logoUrl }
          mall { id name logoUrl }
        }
      }
    }
  }

  fragment StreamCard on Livestream {
    id
    title
    status
    startedAt
    viewerCount
    isLive
    isUpcoming
    isEnded
    store { id name }
    livestreamProducts { product { id title imageUrls } }
  }
`

// ---- Stripe card checkout (signed-in only) ------------------------

export const CREATE_PAYMENT_INTENT = gql`
  mutation ShopCreatePaymentIntent($currency: String) {
    createPaymentIntent(currency: $currency) {
      paymentIntentId
      clientSecret
      amount
      userErrors { field message }
    }
  }
`

export const FINALIZE_STRIPE_CHECKOUT = gql`
  mutation ShopFinalizeStripeCheckout($paymentIntentId: String!) {
    finalizeStripeCheckout(paymentIntentId: $paymentIntentId) {
      order { id orderNumber email status total currency }
      userErrors { field message }
    }
  }
`

export const GET_PRODUCT_FOR_PDP = gql`
  query BuyerGetProduct($id: ID!) {
    product(id: $id) {
      id
      title
      description
      handle
      currency
      status
      imageUrls
      productVariants {
        id
        title
        price
        compareAtPrice
        sku
        option1
        option2
        option3
        inventoryQuantity
        available
        imageUrls
      }
      stores {
        id
        name
      }
    }
  }
`
